/* ═══════════════════════════════════════════════════════════════
   Pricing engine — pure functions, no side effects.
   All numbers flow from rateCard.js. Transparent, auditable maths.
   ═══════════════════════════════════════════════════════════════ */
import {
  RATE_CARD,
  MODIFIER_TABLES,
  CGI_PROJECT_FACTOR,
  BASELINE_MULTIPLIER,
  DISCOUNTS,
  FX_RATES,
  CURRENCY_SYMBOLS,
} from "./rateCard";

/* Convert a USD amount to the target currency and format. */
export function formatMoney(amountUsd, currency = "USD") {
  const rate = FX_RATES[currency] || 1;
  const value = amountUsd * rate;
  const sym = CURRENCY_SYMBOLS[currency] || "$";
  const rounded = Math.round(value);
  const withCommas = rounded.toLocaleString("en-US");
  return `${sym}${withCommas}`;
}

/* Look up modifier multiplier for a given item + tier value. */
function modifierValue(modKey, tierValue) {
  if (!modKey) return 1;
  const table = MODIFIER_TABLES[modKey];
  if (!table) return 1;
  return table[tierValue] ?? 1;
}

/* ─── Per-category priceItem ─────────────────────────────────────
   `input` is the entry the user built in the form.
   Returns { rows: [...], categorySubtotal }.
   Each row = { description, qty, unit, unitRateUsd, lineTotalUsd }.
   ───────────────────────────────────────────────────────────────── */
export function priceLineItem(input) {
  const rows = [];
  const cat = RATE_CARD[input.category];
  if (!cat) return { rows, categorySubtotal: 0 };

  const push = (key, qty, extraLabel = "") => {
    if (!qty || qty <= 0) return;
    const item = cat.lineItems[key];
    if (!item) return;
    const modKey = item.mod;
    const tierVal = input.tier || input.difficulty || input.detail || input.style;
    const mult = modifierValue(modKey, tierVal);
    // CGI-only project factor
    const projFactor = input.category === "cgi3d" && input.projectType
      ? (CGI_PROJECT_FACTOR[input.projectType] ?? 1)
      : 1;
    const unitRate = item.baseRate * mult * projFactor;
    const line = unitRate * qty;
    rows.push({
      description: `${cat.label} · ${labelFor(input.category, key)}${extraLabel ? " · " + extraLabel : ""}`,
      qty,
      unit: item.unit,
      unitRateUsd: unitRate,
      lineTotalUsd: line,
      tier: tierVal || "—",
    });
  };

  if (input.category === "cgi3d") {
    push("runtime", num(input.runtime));
    push("hero", num(input.hero));
    push("secondary", num(input.secondary));
    push("prop", num(input.props));
    push("environment", num(input.environments));
    push("soundDesign", num(input.soundDesign));
  }
  if (input.category === "vfx") {
    const shots = num(input.shots);
    const avgLen = num(input.avgShotLength) || 4;
    const lenFactor = 1 + Math.max(0, avgLen - 4) * (cat.shotLengthFactor || 0);
    const key = input.vfxTask;
    if (key && cat.lineItems[key]) {
      const item = cat.lineItems[key];
      const mult = modifierValue(item.mod, input.difficulty);
      const unitRate = item.baseRate * mult * lenFactor;
      const line = unitRate * shots;
      if (shots > 0) {
        rows.push({
          description: `${cat.label} · ${labelFor("vfx", key)}${avgLen !== 4 ? ` · ${avgLen}s avg` : ""}`,
          qty: shots,
          unit: item.unit,
          unitRateUsd: unitRate,
          lineTotalUsd: line,
          tier: input.difficulty || "Medium",
        });
      }
    }
  }
  if (input.category === "animatic") {
    if (input.mode === "runtime") push("runtime", num(input.runtime));
    else push("panels", num(input.panels));
    push("scene", num(input.scenes));
  }
  if (input.category === "anim2d") {
    push("runtime", num(input.runtime));
    push("character", num(input.characters));
    push("background", num(input.backgrounds));
  }
  if (input.category === "mocap") {
    if (input.mode === "takes") push("takes", num(input.takes));
    else push("seconds", num(input.seconds));
  }

  const categorySubtotal = rows.reduce((s, r) => s + r.lineTotalUsd, 0);
  return { rows, categorySubtotal };
}

/* ─── Aggregate an array of inputs into a top sheet ─────────────── */
export function buildTopSheet(inputs) {
  const perCategory = {};
  let allRows = [];
  let subtotal = 0;
  for (const input of inputs) {
    const { rows, categorySubtotal } = priceLineItem(input);
    if (rows.length === 0) continue;
    const key = input.category;
    if (!perCategory[key]) perCategory[key] = { label: RATE_CARD[key].label, subtotal: 0, rows: [] };
    perCategory[key].subtotal += categorySubtotal;
    perCategory[key].rows.push(...rows);
    allRows = allRows.concat(rows);
    subtotal += categorySubtotal;
  }

  /* Discounts stack ADDITIVELY on the raw subtotal.
     Example: 25% + 15% = 40% off  →  grand total = subtotal × 0.60 */
  const discountLines = DISCOUNTS.map((d) => ({
    id: d.id,
    label: d.label,
    pct: d.pct,
    amountUsd: subtotal * d.pct,
  }));
  const combinedPct = DISCOUNTS.reduce((s, d) => s + d.pct, 0);
  const totalDiscountUsd = subtotal * combinedPct;
  const grandTotalUsd = subtotal - totalDiscountUsd;

  /* Baseline stays derived from the raw subtotal (what the market
     would quote for the same scope). Savings is now vs the discounted
     Vawworld total. */
  const baselineTotalUsd = subtotal * BASELINE_MULTIPLIER;
  const savingsUsd = baselineTotalUsd - grandTotalUsd;
  const savingsPct = baselineTotalUsd > 0 ? (savingsUsd / baselineTotalUsd) * 100 : 0;

  return {
    perCategory,
    rows: allRows,
    subtotalUsd: subtotal,
    discountLines,
    combinedDiscountPct: combinedPct,
    totalDiscountUsd,
    grandTotalUsd,
    baselineTotalUsd,
    savingsUsd,
    savingsPct,
  };
}

/* ─── Presentation helpers ─────────────────────────────────────── */
function num(v) {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

const LABELS = {
  cgi3d: {
    runtime: "Feature/series runtime",
    hero: "Hero character",
    secondary: "Secondary character",
    prop: "Hero prop",
    environment: "Set / environment",
    soundDesign: "Sound design (per finished minute)",
  },
  vfx: {
    wireRemoval: "Wire & rig removal",
    rotoscoping: "Rotoscoping",
    cleanupPaint: "Clean-up / paint-out",
    mattePainting: "Matte painting",
    cgIntegration: "CG integration",
    compositing: "Compositing",
  },
  animatic: { runtime: "Animatic runtime", panels: "Storyboard panels", scene: "Scene block" },
  anim2d: { runtime: "2D runtime", character: "2D character", background: "2D background" },
  mocap: { takes: "Mocap take", seconds: "Mocap footage" },
};

export function labelFor(category, key) {
  return LABELS[category]?.[key] || key;
}
